import React from "react";
import {ZoneTimeRange, ZoneTimeRangeAbsolute, ZoneTimeRangeRelative} from "../typedefs";
import BasicText, {BasicNumber} from "../../../components/form-v2/basic-text";

type TimeUnit = "days" | "weeks" | "months";

const unitOptions = [
    {value: "days", label: "Days"},
    {value: "weeks", label: "Weeks"},
    {value: "months", label: "Months"},
];

type Props = {
    value: ZoneTimeRange;
    onChange: (value: ZoneTimeRange) => void;
}

export function ZoneTimeRangePicker({value, onChange}: Props) {

    const setMode = (mode: "relative" | "absolute") => {
        if (mode === "relative") {
            onChange({mode: "relative", pastValue: 7, pastUnit: "days", futureValue: 0, futureUnit: "days"});
        } else {
            const now = new Date();
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            onChange({
                mode: "absolute",
                from: weekAgo.toISOString().slice(0, 16),
                to: now.toISOString().slice(0, 16),
            });
        }
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="flex rounded-md overflow-hidden shadow-md self-start">
                <button
                    type="button"
                    className={`px-4 py-2 text-sm font-medium transition-colors ${value.mode === "relative" ? "bg-blue text-white" : "bg-white hover:bg-white-blue"}`}
                    onClick={() => setMode("relative")}
                >
                    Relative
                </button>
                <button
                    type="button"
                    className={`px-4 py-2 text-sm font-medium transition-colors border-l border-light-light-grey ${value.mode === "absolute" ? "bg-blue text-white" : "bg-white hover:bg-white-blue"}`}
                    onClick={() => setMode("absolute")}
                >
                    Absolute
                </button>
            </div>

            {value.mode === "relative" ? (
                <RelativePicker value={value} onChange={onChange}/>
            ) : (
                <AbsolutePicker value={value} onChange={onChange}/>
            )}
        </div>
    );
}

const selectCls = "h-10 shadow-md rounded-md px-2 outline-none bg-white focus:outline focus:outline-blue text-sm";

function RelativePicker({value, onChange}: { value: ZoneTimeRangeRelative; onChange: (v: ZoneTimeRange) => void }) {
    return (
        <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
                <label>Back</label>
                <div className="flex gap-2">
                    <BasicNumber
                        type="number"
                        value={value.pastValue}
                        onChange={v => onChange({...value, pastValue: Math.max(0, v)})}
                        min={0}
                        max={999}
                        align="left"
                    />
                    <select
                        value={value.pastUnit}
                        onChange={e => onChange({...value, pastUnit: e.target.value as TimeUnit})}
                        className={selectCls}
                    >
                        {unitOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </div>
            </div>
            <div className="flex flex-col gap-1">
                <label>Forward</label>
                <div className="flex gap-2">
                    <BasicNumber
                        type="number"
                        value={value.futureValue}
                        onChange={v => onChange({...value, futureValue: Math.max(0, v)})}
                        min={0}
                        max={999}
                        align="left"
                    />
                    <select
                        value={value.futureUnit}
                        onChange={e => onChange({...value, futureUnit: e.target.value as TimeUnit})}
                        className={selectCls}
                    >
                        {unitOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </div>
            </div>
        </div>
    );
}

function AbsolutePicker({value, onChange}: { value: ZoneTimeRangeAbsolute; onChange: (v: ZoneTimeRange) => void }) {
    return (
        <div className="grid grid-cols-2 gap-4">
            <BasicText
                label="From"
                type="datetime-local"
                value={value.from}
                onChange={v => onChange({...value, from: v})}
            />
            <BasicText
                label="To"
                type="datetime-local"
                value={value.to}
                onChange={v => onChange({...value, to: v})}
            />
        </div>
    );
}
